import DashboardLayout from '@/components/DashboardLayout'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Pencil,
  CheckCircle2,
  XCircle,
  RotateCcw,
  UserPlus,
  UserMinus,
  Copy,
  Settings,
  Waves,
} from 'lucide-react'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { toast } from 'sonner'
import { useLessonsChannel } from '@/hooks/useLessonsChannel'
import { useAuth } from '@/components/Layout'
import { format, addDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { boardTypeLabel } from '@/lib/equipment'
import type { BoardType } from '@/types/equipment'

interface Teacher {
  id: number
  name: string
}
interface Student {
  id: number
  name: string
}

interface Lesson {
  id: number
  lesson_date: string
  start_time: string
  end_time: string
  description: string
  status: string
  /** Só o dono recebe este valor — o servidor omite para os demais. */
  teacher_payment?: string | null
  checked_in: boolean
  checked_in_at: string | null
  attendance_status: string
  teacher: Teacher | null
  // De qual grade a vaga saiu. A aula reservada perde o `teacher` (é assim que
  // ela entra na fila de associação) mas continua sendo daquele horário — é por
  // este campo que ela segue aparecendo na célula do professor.
  source_teacher?: Teacher | null
  student: Student | null
  board?: LessonBoard | null
  extra_students?: {
    id: number
    name: string | null
    attendance_status?: string
    board?: LessonBoard | null
  }[]
  is_available?: boolean
  pending_association?: boolean
}

interface LessonBoard {
  source: 'own' | 'school' | null
  equipment: {
    id: number
    board_type: string
    size: string | null
    nickname: string | null
  } | null
}

interface Props {
  lessons_by_date: Record<string, Lesson[]>
  current_month: number
  current_year: number
  start_date: string
  end_date: string
  pending_association_count?: number
  pending_association_by_date?: Record<string, number>
  teachers?: Teacher[]
  students?: Student[]
  show_teacher_to_students?: boolean
  operating_hours?: {
    days: number[]
    morning: { start: string; end: string }
    afternoon: { start: string; end: string }
  } | null
}

const DEFAULT_START_HOUR = 6
const DEFAULT_END_HOUR = 20

// Texto curto da prancha de um aluno para exibir no card/Dialog.
// 'own' => prancha própria; 'school' => tipo + tamanho do inventário.
const boardText = (board?: LessonBoard | null): string | null => {
  if (!board) return null
  if (board.source === 'own') return 'Prancha própria'
  if (board.equipment) {
    const eq = board.equipment
    return `${boardTypeLabel(eq.board_type as BoardType)}${eq.size ? ' ' + eq.size : ''}`
  }
  return null
}

const parseStartHour = (time: string) => parseInt(time.split(':')[0])
// Trata "00:00" no fim como 24h (meia-noite) para escolas que vão até o fim do dia.
const parseEndHour = (time: string) => {
  const [h, m] = time.split(':').map(Number)
  if (h === 0 && (m ?? 0) === 0) return 24
  return m && m > 0 ? h + 1 : h
}

export default function Index({
  lessons_by_date,
  teachers = [],
  students = [],
  operating_hours,
}: Props) {
  const { user } = useAuth()
  const [lessonsByDate, setLessonsByDate] = useState(lessons_by_date)

  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const getInitialDate = () => {
    const today = new Date()
    const opDays = operating_hours?.days || [0, 1, 2, 3, 4, 5, 6]
    if (opDays.includes(today.getDay())) return todayStr
    // Find next operating day
    for (let i = 1; i <= 7; i++) {
      const next = addDays(today, i)
      if (opDays.includes(next.getDay())) return format(next, 'yyyy-MM-dd')
    }
    return todayStr
  }
  const [selectedDate, setSelectedDate] = useState(getInitialDate)

  const opDaysConfig = operating_hours?.days || [0, 1, 2, 3, 4, 5, 6]

  // Monday of the current week view
  const [weekMonday, setWeekMonday] = useState(() => {
    const d = new Date(selectedDate + 'T00:00:00')
    const day = d.getDay()
    const diff = (day + 6) % 7 // days since Monday
    const monday = new Date(d)
    monday.setDate(d.getDate() - diff)
    return format(monday, 'yyyy-MM-dd')
  })

  // 7 days of the week (all days, closed days shown with indicator)
  const weekDays = useMemo(() => {
    const monday = new Date(weekMonday + 'T00:00:00')
    return Array.from({ length: 7 }, (_, i) =>
      format(addDays(monday, i), 'yyyy-MM-dd'),
    )
  }, [weekMonday])

  const isDayClosed = (date: string) => {
    const dayOfWeek = new Date(date + 'T00:00:00').getDay()
    return !opDaysConfig.includes(dayOfWeek)
  }

  // Track the loaded month range
  const [loadedMonth, setLoadedMonth] = useState(() => {
    const d = new Date(getInitialDate() + 'T00:00:00')
    return `${d.getFullYear()}-${d.getMonth()}`
  })

  // O controller carrega apenas UM mês de aulas por vez. Como a faixa da semana
  // (Seg–Dom) pode mostrar dias do mês seguinte, qualquer navegação que aterrisse
  // num dia de outro mês precisa recarregar os dados — senão o dia renderiza vazio
  // mesmo com aulas no banco (bug: julho não aparecia clicando direto no dia).
  const ensureMonthLoaded = (date: string) => {
    const d = new Date(date + 'T00:00:00')
    const monthKey = `${d.getFullYear()}-${d.getMonth()}`
    if (monthKey === loadedMonth) return
    setLoadedMonth(monthKey)
    router.get(
      '/lessons',
      {
        month: d.getMonth() + 1,
        year: d.getFullYear(),
      },
      { preserveState: true, preserveScroll: true, only: ['lessons_by_date'] },
    )
  }

  const navigateWeek = (dir: number) => {
    const current = new Date(weekMonday + 'T00:00:00')
    const next = addDays(current, dir * 7)
    const newMonday = format(next, 'yyyy-MM-dd')
    setWeekMonday(newMonday)
    // Auto-select first operating day of new week
    const allDays = Array.from({ length: 7 }, (_, i) =>
      format(addDays(next, i), 'yyyy-MM-dd'),
    )
    const firstOpDay = allDays.find((date) =>
      opDaysConfig.includes(new Date(date + 'T00:00:00').getDay()),
    )
    const newSelected = firstOpDay || format(next, 'yyyy-MM-dd')
    setSelectedDate(newSelected)
    ensureMonthLoaded(newSelected)
  }

  // Selected lesson for sidebar
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null)
  // Quick-add: id da vaga (Lesson) que o admin esta enchendo com um aluno via sidebar
  const [quickAddVagaId, setQuickAddVagaId] = useState<number | null>(null)
  const [quickAddSearch, setQuickAddSearch] = useState('')
  // Selection mode
  const [selectedSlotIds, setSelectedSlotIds] = useState<number[]>([])
  const [selectionMode, setSelectionMode] = useState(false)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [lessonToDelete, setLessonToDelete] = useState<number | null>(null)
  const [lessonToRelease, setLessonToRelease] = useState<number | null>(null)
  const [associateOpen, setAssociateOpen] = useState(false)
  const [associateTeacherId, setAssociateTeacherId] = useState<number | null>(
    null,
  )
  const [confirmCheckinId, setConfirmCheckinId] = useState<number | null>(null)
  const [confirmCheckinAction, setConfirmCheckinAction] = useState('')

  // Quick create
  const [createOpen, setCreateOpen] = useState(false)
  const [createStartTime, setCreateStartTime] = useState('')
  const [createEndTime, setCreateEndTime] = useState('')
  const [createTeacherId, setCreateTeacherId] = useState('')
  const [createTeacherIds, setCreateTeacherIds] = useState<string[]>([])
  const [createMode, setCreateMode] = useState<'availability' | 'lesson'>(
    'availability',
  )
  const [createStudentId, setCreateStudentId] = useState('')
  const [createStudentFilter, setCreateStudentFilter] = useState('')

  // Operating hours
  const [hoursOpen, setHoursOpen] = useState(false)
  const [opDays, setOpDays] = useState<number[]>(
    operating_hours?.days || [1, 2, 3, 4, 5, 6],
  )
  const [morningStart, setMorningStart] = useState(
    operating_hours?.morning?.start || '06:00',
  )
  const [morningEnd, setMorningEnd] = useState(
    operating_hours?.morning?.end || '11:00',
  )
  const [afternoonStart, setAfternoonStart] = useState(
    operating_hours?.afternoon?.start || '14:00',
  )
  const [afternoonEnd, setAfternoonEnd] = useState(
    operating_hours?.afternoon?.end || '18:00',
  )

  const toggleOpDay = (day: number) => {
    setOpDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort(),
    )
  }

  const saveOperatingHours = () => {
    router.patch(
      '/school/operating_hours',
      {
        operating_days: opDays,
        morning_start: morningStart,
        morning_end: morningEnd,
        afternoon_start: afternoonStart,
        afternoon_end: afternoonEnd,
      },
      {
        onSuccess: () => {
          setHoursOpen(false)
          toast.success('Horarios salvos!')
        },
      },
    )
  }

  // Replicate
  const [replicateOpen, setReplicateOpen] = useState(false)
  const [replicateTargetDates, setReplicateTargetDates] = useState<string[]>([])

  useEffect(() => {
    setLessonsByDate(lessons_by_date)
    // Update selectedLesson with fresh data after reload
    setSelectedLesson((prev) => {
      if (!prev) return null
      const dayLessons = lessons_by_date[prev.lesson_date] || []
      return dayLessons.find((l) => l.id === prev.id) || null
    })
  }, [lessons_by_date])

  // ── WebSocket ──
  const onLessonCreated = useCallback((lesson: Lesson) => {
    setLessonsByDate((prev) => {
      const k = lesson.lesson_date
      const existing = prev[k] || []
      if (existing.some((l) => l.id === lesson.id)) return prev
      return {
        ...prev,
        [k]: [...existing, lesson].sort((a, b) =>
          a.start_time.localeCompare(b.start_time),
        ),
      }
    })
  }, [])

  const onLessonUpdated = useCallback((lesson: Lesson) => {
    if (lesson.student && !lesson.teacher)
      toast.info('Novo agendamento!', {
        description: `${lesson.student.name} agendou um horario.`,
        duration: 6000,
      })
    setLessonsByDate((prev) => {
      const k = lesson.lesson_date
      const existing = prev[k] || []
      const updated = existing.map((l) => (l.id === lesson.id ? lesson : l))
      if (!existing.some((l) => l.id === lesson.id)) {
        updated.push(lesson)
        updated.sort((a, b) => a.start_time.localeCompare(b.start_time))
      }
      return { ...prev, [k]: updated }
    })
  }, [])

  const onLessonDeleted = useCallback(
    (lessonId: number) => {
      setLessonsByDate((prev) => {
        const n = { ...prev }
        Object.keys(n).forEach((k) => {
          n[k] = n[k].filter((l) => l.id !== lessonId)
        })
        return n
      })
      if (selectedLesson?.id === lessonId) setSelectedLesson(null)
    },
    [selectedLesson],
  )

  useLessonsChannel({
    schoolId: user?.school?.id,
    onLessonCreated,
    onLessonUpdated,
    onLessonDeleted,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible')
        router.reload({ only: ['lessons_by_date'] })
    }, 45000)
    return () => clearInterval(interval)
  }, [])

  // ── Navigation ──
  const selectDay = (date: string) => {
    setSelectedDate(date)
    setSelectedLesson(null)
    setSelectedSlotIds([])
    // Recarrega se o dia clicado for de um mês ainda não carregado (ex.: dias do
    // mês seguinte que aparecem na mesma semana visível).
    ensureMonthLoaded(date)
  }

  // ── Day data ──
  const dayLessons = useMemo(
    () => lessonsByDate[selectedDate] || [],
    [lessonsByDate, selectedDate],
  )

  // Constrói a grade dinamicamente. As linhas são SLOTS de horário reais ("HH:MM"),
  // não horas cheias — assim uma aula que começa 05:30 aparece na linha "05:30" e não
  // some sob um rótulo "05:00". Os slots vêm dos horários reais das aulas do dia, unidos
  // com as horas cheias do funcionamento (para permitir criar em horários ainda vazios).
  const activeSlots = useMemo(() => {
    const wholeHours: number[] = (() => {
      if (!operating_hours) {
        return Array.from(
          { length: DEFAULT_END_HOUR - DEFAULT_START_HOUR + 1 },
          (_, i) => i + DEFAULT_START_HOUR,
        )
      }
      const mStart = parseStartHour(operating_hours.morning.start)
      const mEnd = parseEndHour(operating_hours.morning.end)
      const aStart = parseStartHour(operating_hours.afternoon.start)
      const aEnd = parseEndHour(operating_hours.afternoon.end)
      const minHour = Math.min(mStart, aStart)
      const maxHour = Math.max(mEnd, aEnd)
      const range = Array.from(
        { length: Math.max(0, maxHour - minHour) },
        (_, i) => i + minHour,
      )
      return range.filter(
        (h) => (h >= mStart && h < mEnd) || (h >= aStart && h < aEnd),
      )
    })()

    // Slots reais presentes nas aulas do dia (ex: "05:30", "08:00").
    const lessonSlots = new Set(dayLessons.map((l) => l.start_time.slice(0, 5)))
    const lessonHours = new Set(
      Array.from(lessonSlots).map((s) => parseInt(s.split(':')[0])),
    )

    const slots = new Set<string>(lessonSlots)
    // Só adiciona a hora cheia "HH:00" se não houver nenhuma aula naquela hora
    // (evita mostrar 05:00 fantasma quando a grade real é 05:30).
    wholeHours.forEach((h) => {
      if (!lessonHours.has(h)) slots.add(`${String(h).padStart(2, '0')}:00`)
    })

    return Array.from(slots).sort()
  }, [operating_hours, dayLessons])

  const dayTeachers = useMemo(() => {
    const teacherIds = new Set<number>()
    dayLessons.forEach((l) => {
      if (l.teacher) teacherIds.add(l.teacher.id)
    })
    // Include all school teachers, sorted with active ones first
    return teachers
      .map((t) => ({
        ...t,
        hasLessons: teacherIds.has(t.id),
      }))
      .sort((a, b) => (b.hasLessons ? 1 : 0) - (a.hasLessons ? 1 : 0))
  }, [dayLessons, teachers])

  const pendingLessons = useMemo(
    () => dayLessons.filter((l) => l.pending_association),
    [dayLessons],
  )

  const dayStats = useMemo(() => {
    const booked = dayLessons.filter((l) => l.student && l.teacher).length
    const available = dayLessons.filter((l) => l.is_available).length
    const pending = pendingLessons.length
    return { booked, available, pending, total: dayLessons.length }
  }, [dayLessons, pendingLessons])

  // ── Get lesson(s) at teacher+slot ──
  // slot é "HH:MM" (ex: "05:30"). Pode haver várias Lessons paralelas (1 por vaga)
  // no MESMO horário exato — getLessonsAt retorna todas.
  //
  // SÓ aulas com professor: a reserva aguardando associação aparece num único
  // lugar, a coluna "Sem Professor" — nenhum professor foi escolhido, e quem
  // escolhe é o admin. A procedência (source_teacher) existe apenas para o
  // GERADOR não recriar a vaga consumida; ela não pinta célula nenhuma.
  const getLessonsAt = (teacherId: number, slot: string) => {
    return dayLessons.filter(
      (l) => l.teacher?.id === teacherId && l.start_time.slice(0, 5) === slot,
    )
  }

  // A coluna "Sem Professor" é o ÚNICO lugar onde a aula aguardando associação
  // aparece — é lá que o admin clica para escalar alguém, e é decisão dele,
  // nunca do sistema.
  const getPendingAt = (slot: string) => {
    return pendingLessons.filter((l) => l.start_time.slice(0, 5) === slot)
  }

  const hasPending = pendingLessons.length > 0

  const getCardStyle = (lesson: Lesson) => {
    if (lesson.attendance_status === 'present')
      return 'bg-green-100 border-green-300 text-green-800 dark:bg-green-950/30 dark:border-green-800 dark:text-green-300'
    if (lesson.attendance_status === 'absent')
      return 'bg-red-100 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-300'
    if (lesson.is_available)
      return 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-400'
    return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-300'
  }

  // ── Actions ──
  const handleDelete = (id: number) => {
    setLessonToDelete(id)
    setDeleteDialogOpen(true)
  }
  const confirmDelete = () => {
    if (!lessonToDelete) return
    router.delete(`/lessons/${lessonToDelete}`, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        setLessonToDelete(null)
        setSelectedLesson(null)
      },
    })
  }

  // Tira só o aluno, mantendo professor, dia e horário — a vaga volta a ficar
  // disponível. Antes disso o único caminho era excluir a aula inteira e abrir
  // outra vaga na mão.
  const confirmRelease = () => {
    if (!lessonToRelease) return
    router.patch(
      `/lessons/${lessonToRelease}/release_student`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          setLessonToRelease(null)
          setSelectedLesson(null)
        },
        onError: (errors) =>
          toast.error(errors.base || 'Não foi possível liberar a vaga.'),
      },
    )
  }

  const confirmBulkDelete = () => {
    if (selectedSlotIds.length === 0) return
    const idsParam = selectedSlotIds.map((id) => `ids[]=${id}`).join('&')
    router.delete(`/lessons/bulk_destroy?${idsParam}&date=${selectedDate}`, {
      onSuccess: () => {
        setBulkDeleteOpen(false)
        setSelectedSlotIds([])
        setSelectionMode(false)
        setSelectedLesson(null)
      },
    })
  }

  const selectAllDay = () => {
    setSelectedSlotIds(dayLessons.map((l) => l.id))
  }

  const handleCheckin = (id: number, action: string) => {
    setConfirmCheckinId(id)
    setConfirmCheckinAction(action)
  }
  const confirmCheckin = () => {
    if (!confirmCheckinId) return
    router.post(
      `/lessons/${confirmCheckinId}/${confirmCheckinAction}`,
      {},
      {
        onSuccess: () => {
          setConfirmCheckinId(null)
          setConfirmCheckinAction('')
          router.reload({ only: ['lessons_by_date'] })
        },
      },
    )
  }

  const handleAssociate = (lesson: Lesson) => {
    setSelectedLesson(lesson)
    // Nenhum professor vem marcado: a escola não mostra professor ao aluno
    // justamente porque a escalação é decisão do ADMIN — pré-marcar induziria a
    // escolha. A procedência aparece só como rótulo ("vaga de origem").
    setAssociateTeacherId(null)
    setAssociateOpen(true)
  }
  const confirmAssociate = () => {
    if (!selectedLesson || !associateTeacherId) return
    router.patch(
      `/lessons/${selectedLesson.id}/associate_teacher`,
      { teacher_id: associateTeacherId },
      {
        onSuccess: () => {
          setAssociateOpen(false)
          toast.success('Professor associado!')
        },
        onError: (errors) => {
          toast.error(errors.base || 'Não foi possível associar o professor.')
        },
      },
    )
  }

  const openQuickCreate = (teacherId: number | null, slot: string) => {
    const [h, m] = slot.split(':').map(Number)
    setCreateStartTime(slot)
    setCreateEndTime(
      `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
    )
    setCreateTeacherId(teacherId ? String(teacherId) : '')
    setCreateTeacherIds(teacherId ? [String(teacherId)] : [])
    setCreateStudentId('')
    setCreateStudentFilter('')
    setCreateMode('availability')
    setCreateOpen(true)
  }

  const submitQuickCreate = () => {
    if (createMode === 'availability' && createTeacherIds.length === 0) {
      toast.error('Selecione pelo menos um professor')
      return
    }
    if (createMode === 'lesson' && !createTeacherId) {
      toast.error('Selecione um professor')
      return
    }

    const payload =
      createMode === 'availability'
        ? {
            lesson: {
              lesson_date: selectedDate,
              start_time: createStartTime,
              end_time: createEndTime,
              teacher_payment: 0,
            },
            teacher_ids: createTeacherIds,
          }
        : {
            lesson: {
              lesson_date: selectedDate,
              start_time: createStartTime,
              end_time: createEndTime,
              teacher_id: createTeacherId,
              student_id: createStudentId || undefined,
              teacher_payment: 0,
            },
          }

    router.post('/lessons', payload, {
      onSuccess: () => setCreateOpen(false),
      onError: (errors) => toast.error(Object.values(errors).flat().join(', ')),
    })
  }

  const toggleCreateTeacher = (id: string) => {
    setCreateTeacherIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  // ── Replicate ──
  const toggleSlotSelection = (lessonId: number) => {
    setSelectedSlotIds((prev) =>
      prev.includes(lessonId)
        ? prev.filter((x) => x !== lessonId)
        : [...prev, lessonId],
    )
  }

  const openReplicate = () => {
    const slots = selectionMode
      ? selectedSlotIds
      : dayLessons.filter((l) => l.is_available).map((l) => l.id)
    if (slots.length === 0) {
      toast.error('Nenhum horario disponivel para replicar')
      return
    }
    setSelectedSlotIds(slots)
    setReplicateTargetDates([])
    setReplicateOpen(true)
  }

  const toggleReplicateDate = (date: string) => {
    setReplicateTargetDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    )
  }

  const confirmReplicate = () => {
    if (replicateTargetDates.length === 0) {
      toast.error('Selecione pelo menos um dia destino')
      return
    }
    const lessonsToReplicate = dayLessons.filter((l) =>
      selectedSlotIds.includes(l.id),
    )

    let created = 0
    const promises = replicateTargetDates
      .map((targetDate) =>
        lessonsToReplicate.map((lesson) =>
          fetch('/lessons', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token':
                document
                  .querySelector('meta[name="csrf-token"]')
                  ?.getAttribute('content') || '',
            },
            body: JSON.stringify({
              lesson: {
                lesson_date: targetDate,
                start_time: lesson.start_time,
                end_time: lesson.end_time,
                teacher_id: lesson.teacher?.id,
                teacher_payment: lesson.teacher_payment || 0,
              },
            }),
          }).then((r) => {
            if (r.ok) created++
          }),
        ),
      )
      .flat()

    Promise.all(promises).then(() => {
      setReplicateOpen(false)
      setSelectionMode(false)
      setSelectedSlotIds([])
      toast.success(`${created} horarios replicados!`)
      router.reload({ only: ['lessons_by_date'] })
    })
  }

  // Next 14 days for replicate date picker (excluding selected date)
  const replicateDateOptions = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => {
      const d = addDays(new Date(selectedDate + 'T00:00:00'), i + 1)
      return format(d, 'yyyy-MM-dd')
    }).filter((date) => {
      const dayOfWeek = new Date(date + 'T00:00:00').getDay()
      return opDaysConfig.includes(dayOfWeek)
    })
  }, [selectedDate, opDaysConfig])

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Agenda</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setHoursOpen(true)}
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const today = new Date()
                const diff = (today.getDay() + 6) % 7
                const monday = new Date(today)
                monday.setDate(today.getDate() - diff)
                setWeekMonday(format(monday, 'yyyy-MM-dd'))
                setSelectedDate(todayStr)
                setSelectedLesson(null)
              }}
            >
              Hoje
            </Button>
            {selectionMode ? (
              <>
                <Button variant="ghost" size="sm" onClick={selectAllDay}>
                  Selecionar Tudo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectionMode(false)
                    setSelectedSlotIds([])
                  }}
                >
                  Cancelar
                </Button>
                {selectedSlotIds.length > 0 && (
                  <>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setBulkDeleteOpen(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Excluir (
                      {selectedSlotIds.length})
                    </Button>
                    <Button size="sm" onClick={openReplicate}>
                      <Copy className="w-4 h-4 mr-1" /> Replicar (
                      {selectedSlotIds.length})
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectionMode(true)}
                >
                  Selecionar
                </Button>
                <Button variant="outline" size="sm" onClick={openReplicate}>
                  <Copy className="w-4 h-4 mr-1" /> Copiar Dia
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Week strip */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => navigateWeek(-1)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex flex-1 gap-1">
            {weekDays.map((date) => {
              const d = new Date(date + 'T00:00:00')
              const isToday = date === todayStr
              const isSelected = date === selectedDate
              const closed = isDayClosed(date)
              const lessons = lessonsByDate[date] || []
              const bookedCount = lessons.filter((l) => l.student).length
              const availableCount = lessons.filter(
                (l) => l.is_available,
              ).length
              const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
              return (
                <button
                  key={date}
                  onClick={() => selectDay(date)}
                  className={`flex-1 py-2.5 rounded-lg text-center transition-all relative ${
                    isSelected
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : closed
                        ? 'bg-muted/50 opacity-60 border border-dashed border-muted-foreground/20'
                        : isToday
                          ? 'bg-primary/10 border border-primary/30'
                          : 'hover:bg-muted border border-transparent'
                  }`}
                >
                  <p
                    className={`text-xs ${isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}
                  >
                    {isToday ? 'Hoje' : dayNames[d.getDay()]}
                  </p>
                  <p
                    className={`text-xl font-bold ${isSelected ? '' : closed ? 'text-muted-foreground' : isToday ? 'text-primary' : ''}`}
                  >
                    {d.getDate()}
                  </p>
                  <p
                    className={`text-[10px] ${isSelected ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}
                  >
                    {closed && !isSelected
                      ? 'Fechado'
                      : format(d, 'MMM', { locale: ptBR })}
                  </p>
                  <div className="flex justify-center gap-1 mt-1">
                    {bookedCount > 0 && (
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground/60' : 'bg-blue-500'}`}
                      />
                    )}
                    {availableCount > 0 && (
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground/40' : 'bg-emerald-400'}`}
                      />
                    )}
                  </div>
                </button>
              )
            })}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => navigateWeek(1)}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Day info + stats */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-sm font-semibold capitalize">
              {format(
                new Date(selectedDate + 'T00:00:00'),
                "EEEE, dd 'de' MMMM",
                { locale: ptBR },
              )}
            </span>
          </div>
          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-400" />{' '}
              {dayStats.booked} aulas
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />{' '}
              {dayStats.available} livres
            </span>
            {dayStats.pending > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400" />{' '}
                {dayStats.pending} s/ prof
              </span>
            )}
          </div>
        </div>

        {/* Closed day warning */}
        {isDayClosed(selectedDate) && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5 mb-4">
            <Calendar className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-300">
              A escola nao funciona neste dia. Aulas criadas aqui sao
              excepcionais.
            </span>
          </div>
        )}

        {/* Pending association alert */}
        {pendingLessons.length > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5 mb-4">
            <UserPlus className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-300">
              {pendingLessons.length} aula(s) aguardando professor
            </span>
            <div className="flex-1" />
            {pendingLessons.map((l) => (
              <Button
                key={l.id}
                size="sm"
                variant="outline"
                className="h-7 text-xs border-amber-300"
                onClick={() => handleAssociate(l)}
              >
                {l.start_time} {l.student?.name?.split(' ')[0]}
              </Button>
            ))}
          </div>
        )}

        {/* Main: grid full-width (detalhes da aula abrem em Dialog) */}
        <div>
          {/* Day grid: teachers as columns */}
          <div className="border rounded-lg overflow-auto w-full">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted/30">
                  <th className="w-16 p-2 border-r text-xs text-muted-foreground font-medium">
                    Hora
                  </th>
                  {hasPending && (
                    <th className="p-2 border-r text-xs font-medium min-w-[120px] bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400">
                      Sem Professor
                      <Badge
                        variant="secondary"
                        className="ml-1 text-[10px] h-4"
                      >
                        {pendingLessons.length}
                      </Badge>
                    </th>
                  )}
                  {dayTeachers.map((t) => (
                    <th
                      key={t.id}
                      className="p-2 border-r last:border-r-0 text-xs font-medium min-w-[120px]"
                    >
                      {t.name.split(' ').slice(0, 2).join(' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeSlots.map((slot) => (
                  <tr key={slot} className="border-t">
                    <td className="p-2 border-r text-center">
                      <span className="text-xs text-muted-foreground font-mono">
                        {slot}
                      </span>
                    </td>
                    {hasPending && (
                      <td className="p-1 border-r h-16 align-top bg-amber-50/50 dark:bg-amber-950/10">
                        {getPendingAt(slot).map((lesson) => (
                          <button
                            key={lesson.id}
                            className={`w-full text-left rounded-md border border-amber-300 bg-amber-100 dark:bg-amber-950/30 px-2 py-1.5 text-xs cursor-pointer hover:ring-1 hover:ring-amber-400 transition-all mb-0.5 ${selectedLesson?.id === lesson.id ? 'ring-2 ring-primary' : ''}`}
                            onClick={() =>
                              selectionMode
                                ? toggleSlotSelection(lesson.id)
                                : setSelectedLesson(lesson)
                            }
                          >
                            <p className="font-medium truncate text-amber-800 dark:text-amber-300">
                              {lesson.student?.name?.split(' ')[0]}
                            </p>
                            <p className="text-[10px] text-amber-600">
                              Clicar pra associar
                            </p>
                          </button>
                        ))}
                      </td>
                    )}
                    {dayTeachers.map((teacher) => {
                      const lessonsHere = getLessonsAt(teacher.id, slot)
                      const lesson = lessonsHere[0]
                      const isSelected =
                        lesson && selectedSlotIds.includes(lesson.id)
                      const isMulti = lessonsHere.length > 1
                      // Vagas usadas = aluno principal + cada aluno extra (cada um ocupa 1 vaga)
                      const filledCount = lessonsHere.reduce(
                        (sum, l) =>
                          sum +
                          (l.student ? 1 : 0) +
                          (l.extra_students?.length || 0),
                        0,
                      )
                      const totalNames = lessonsHere.flatMap((l) => {
                        const names: string[] = []
                        // Aula sem professor: o aluno aparece só na coluna
                        // "Sem Professor", onde o admin associa. Aqui a vaga
                        // entra como marcador neutro, sem nome.
                        if (!l.teacher) return names
                        if (l.student?.name)
                          names.push(l.student.name.split(' ')[0])
                        ;(l.extra_students || []).forEach((e) => {
                          if (e.name) names.push(e.name.split(' ')[0])
                        })
                        return names
                      })
                      const isHere =
                        selectedLesson &&
                        lessonsHere.some((l) => l.id === selectedLesson.id)

                      // Estilo agregado
                      const aggPct =
                        lessonsHere.length > 0
                          ? Math.round((filledCount / lessonsHere.length) * 100)
                          : 0
                      const aggStyle = isMulti
                        ? aggPct === 0
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : aggPct >= 90
                            ? 'bg-amber-100 border-amber-300 text-amber-800'
                            : 'bg-blue-50 border-blue-200 text-blue-800'
                        : ''

                      return (
                        <td
                          key={teacher.id}
                          className={`p-1 border-r last:border-r-0 h-16 align-top ${isSelected ? 'bg-primary/10' : ''}`}
                        >
                          {isMulti ? (
                            <button
                              className={`w-full text-left rounded-md border px-2 py-1.5 text-xs cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all ${aggStyle} ${isHere ? 'ring-2 ring-primary' : ''}`}
                              onClick={() =>
                                selectionMode ? null : setSelectedLesson(lesson)
                              }
                            >
                              <p className="font-semibold">
                                {filledCount}/{lessonsHere.length} vagas
                              </p>
                              {totalNames.length > 0 && (
                                <p className="text-[10px] opacity-80 truncate">
                                  {totalNames.slice(0, 2).join(', ')}
                                  {totalNames.length > 2 &&
                                    ` +${totalNames.length - 2}`}
                                </p>
                              )}
                              {totalNames.length === 0 && (
                                <p className="text-[10px] opacity-70">
                                  Disponivel
                                </p>
                              )}
                            </button>
                          ) : lesson ? (
                            <button
                              className={`w-full text-left rounded-md border px-2 py-1.5 text-xs cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all ${getCardStyle(lesson)} ${selectedLesson?.id === lesson.id ? 'ring-2 ring-primary' : ''}`}
                              onClick={() =>
                                selectionMode
                                  ? toggleSlotSelection(lesson.id)
                                  : setSelectedLesson(lesson)
                              }
                            >
                              {selectionMode && (
                                <Checkbox
                                  checked={isSelected}
                                  className="float-right mt-0.5 ml-1"
                                />
                              )}
                              {lesson.student ? (
                                <>
                                  <p className="font-medium truncate">
                                    {lesson.student.name.split(' ')[0]}
                                  </p>
                                  {lesson.attendance_status === 'present' && (
                                    <p className="text-[10px] opacity-70">
                                      Presente
                                    </p>
                                  )}
                                  {lesson.attendance_status === 'absent' && (
                                    <p className="text-[10px] opacity-70">
                                      Faltou
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="truncate">Disponivel</p>
                              )}
                            </button>
                          ) : (
                            <button
                              className="w-full h-full rounded-md border border-dashed border-transparent hover:border-primary/30 hover:bg-primary/5 transition-all flex items-center justify-center group"
                              onClick={() => openQuickCreate(teacher.id, slot)}
                            >
                              <Plus className="w-3.5 h-3.5 text-muted-foreground/20 group-hover:text-primary transition-colors" />
                            </button>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Dialog: detalhes da aula / vagas do horário */}
          {selectedLesson && (
            <Dialog
              open
              onOpenChange={(open) => {
                if (!open) setSelectedLesson(null)
              }}
            >
              <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-base">
                    {(() => {
                      const parallels = selectedLesson.teacher
                        ? getLessonsAt(
                            selectedLesson.teacher.id,
                            selectedLesson.start_time.slice(0, 5),
                          )
                        : [selectedLesson]
                      return parallels.length > 1
                        ? `Vagas do horário (${parallels.length})`
                        : 'Detalhes da aula'
                    })()}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div
                    className={`rounded-lg p-3 border ${getCardStyle(selectedLesson)}`}
                  >
                    <p className="text-lg font-bold font-mono">
                      {selectedLesson.start_time} - {selectedLesson.end_time}
                    </p>
                    <p className="text-xs mt-0.5 opacity-80">
                      {selectedLesson.teacher?.name || (
                        <span className="text-amber-600">
                          Professor a definir
                        </span>
                      )}
                    </p>
                  </div>

                  {(() => {
                    const parallels = selectedLesson.teacher
                      ? getLessonsAt(
                          selectedLesson.teacher.id,
                          selectedLesson.start_time.slice(0, 5),
                        )
                      : [selectedLesson]

                    if (parallels.length > 1) {
                      // Total de vagas = quantidade de Lessons paralelas (capacidade fixa).
                      // 1 card por pessoa (main ou extra) + cards "Disponivel" pra completar
                      // ate a capacidade. Extras NAO inflam o total: ocupam vagas
                      // existentes mesmo estando "compartilhadas" na mesma Lesson.
                      type Card =
                        | { kind: 'main'; lesson: Lesson; studentName: string }
                        | {
                            kind: 'extra'
                            lesson: Lesson
                            studentName: string
                            extraId: number
                          }
                        | { kind: 'empty'; lesson: Lesson }
                      const totalSlots = parallels.length
                      const studentCards: Card[] = []
                      parallels.forEach((l) => {
                        if (l.student) {
                          studentCards.push({
                            kind: 'main',
                            lesson: l,
                            studentName: l.student.name,
                          })
                        }
                        ;(l.extra_students || []).forEach((e) => {
                          if (e.name)
                            studentCards.push({
                              kind: 'extra',
                              lesson: l,
                              studentName: e.name,
                              extraId: e.id,
                            })
                        })
                      })
                      const emptyLessons = parallels.filter(
                        (l) =>
                          !l.student &&
                          (!l.extra_students || l.extra_students.length === 0),
                      )
                      const emptySlotsNeeded = Math.max(
                        totalSlots - studentCards.length,
                        0,
                      )
                      const emptyCards: Card[] = emptyLessons
                        .slice(0, emptySlotsNeeded)
                        .map((l) => ({ kind: 'empty' as const, lesson: l }))
                      const cards: Card[] = [...studentCards, ...emptyCards]
                      const filledCount = studentCards.length
                      return (
                        <>
                          <div className="rounded-lg border p-2 bg-muted/30">
                            <p className="text-xs">
                              <strong>
                                {filledCount}/{totalSlots}
                              </strong>{' '}
                              vagas preenchidas
                            </p>
                            {filledCount > totalSlots && (
                              <p className="text-[10px] text-amber-600 mt-0.5">
                                ⚠ Alguns alunos estão na mesma vaga. Edite pra
                                distribuir.
                              </p>
                            )}
                          </div>
                          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
                            {cards.map((c, idx) => {
                              if (c.kind === 'empty') {
                                return (
                                  <div
                                    key={`empty-${c.lesson.id}`}
                                    className="rounded-md border p-2 text-xs bg-emerald-50/40 border-emerald-200"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="text-[10px] uppercase text-muted-foreground">
                                          Vaga {idx + 1}
                                        </p>
                                        <p className="text-sm font-medium text-emerald-700">
                                          Disponível
                                        </p>
                                      </div>
                                      <div className="flex flex-col gap-1 shrink-0">
                                        <Button
                                          size="icon"
                                          variant="default"
                                          className="h-6 w-6"
                                          onClick={() => {
                                            setQuickAddVagaId(c.lesson.id)
                                            setQuickAddSearch('')
                                          }}
                                          title="Adicionar aluno a esta vaga"
                                        >
                                          <UserPlus className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6 text-destructive"
                                          onClick={() =>
                                            handleDelete(c.lesson.id)
                                          }
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )
                              }
                              // Pra cards de pessoa: pega studentId pra montar a URL de attendance.
                              // Pro main usa lesson.student.id; pro extra acha pelo nome (best effort).
                              let studentIdForAttendance: number | null = null
                              if (c.kind === 'main')
                                studentIdForAttendance =
                                  c.lesson.student?.id ?? null
                              if (c.kind === 'extra')
                                studentIdForAttendance = c.extraId // extraId = student_id
                              const personAttendance =
                                c.kind === 'main'
                                  ? c.lesson.attendance_status
                                  : (c.lesson.extra_students || []).find(
                                      (e) => e.id === c.extraId,
                                    )?.attendance_status
                              const hasAttendance =
                                personAttendance === 'present' ||
                                personAttendance === 'absent'
                              const setPersonAttendance = (
                                status: 'present' | 'absent' | 'pending',
                              ) => {
                                if (!studentIdForAttendance) return
                                router.patch(
                                  `/users/${studentIdForAttendance}/lessons/${c.lesson.id}/attendance`,
                                  {
                                    attendance_status: status,
                                  },
                                  {
                                    preserveScroll: true,
                                    preserveState: true,
                                    onSuccess: () =>
                                      router.reload({
                                        only: ['lessons_by_date'],
                                      }),
                                  },
                                )
                              }
                              return (
                                <div
                                  key={`${c.kind}-${c.lesson.id}-${'extraId' in c ? c.extraId : 'main'}`}
                                  className="rounded-md border p-2 text-xs bg-background"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[10px] uppercase text-muted-foreground">
                                        Vaga {idx + 1}
                                        {c.kind === 'extra' && (
                                          <span className="ml-1 text-amber-600">
                                            (compartilhada)
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-sm font-medium truncate">
                                        {c.studentName}
                                      </p>
                                      {(() => {
                                        const b =
                                          c.kind === 'main'
                                            ? c.lesson.board
                                            : (
                                                c.lesson.extra_students || []
                                              ).find((e) => e.id === c.extraId)
                                                ?.board
                                        const t = boardText(b)
                                        return t ? (
                                          <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                            <Waves className="w-2.5 h-2.5 shrink-0" />
                                            {t}
                                          </p>
                                        ) : null
                                      })()}
                                      {/* Botoes de presenca / status */}
                                      <div className="mt-1.5 flex items-center gap-1">
                                        {hasAttendance ? (
                                          <>
                                            <Badge
                                              variant={
                                                personAttendance === 'present'
                                                  ? 'default'
                                                  : 'destructive'
                                              }
                                              className="text-[9px] h-4 px-1.5"
                                            >
                                              {personAttendance === 'present'
                                                ? 'Presente'
                                                : 'Faltou'}
                                            </Badge>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-5 px-1.5 text-[10px]"
                                              onClick={() =>
                                                setPersonAttendance('pending')
                                              }
                                            >
                                              <RotateCcw className="w-2.5 h-2.5 mr-0.5" />
                                              Resetar
                                            </Button>
                                          </>
                                        ) : (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-6 px-2 text-[10px] text-green-700 hover:bg-green-50"
                                              onClick={() =>
                                                setPersonAttendance('present')
                                              }
                                            >
                                              <CheckCircle2 className="w-3 h-3 mr-0.5" />
                                              Presente
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-6 px-2 text-[10px] text-red-700 hover:bg-red-50"
                                              onClick={() =>
                                                setPersonAttendance('absent')
                                              }
                                            >
                                              <XCircle className="w-3 h-3 mr-0.5" />
                                              Faltou
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1 shrink-0">
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-6 w-6"
                                        onClick={() =>
                                          router.visit(
                                            `/lessons/${c.lesson.id}/edit`,
                                          )
                                        }
                                        title="Editar"
                                      >
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-6 w-6 text-destructive"
                                        onClick={() =>
                                          handleDelete(c.lesson.id)
                                        }
                                        title="Excluir vaga"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </>
                      )
                    }

                    return (
                      <div className="space-y-2">
                        <div>
                          <p className="text-[10px] uppercase text-muted-foreground font-semibold">
                            {selectedLesson.extra_students &&
                            selectedLesson.extra_students.length > 0
                              ? 'Alunos'
                              : 'Aluno'}
                          </p>
                          <p className="text-sm font-medium">
                            {selectedLesson.student?.name || (
                              <span className="text-emerald-600">
                                Vaga disponivel
                              </span>
                            )}
                          </p>
                          {boardText(selectedLesson.board) && (
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Waves className="w-3 h-3 shrink-0" />
                              {boardText(selectedLesson.board)}
                            </p>
                          )}
                          {selectedLesson.extra_students &&
                            selectedLesson.extra_students.length > 0 && (
                              <ul className="mt-1 space-y-0.5">
                                {selectedLesson.extra_students.map((s) => (
                                  <li
                                    key={s.id}
                                    className="text-sm font-medium"
                                  >
                                    {s.name}
                                    {boardText(s.board) && (
                                      <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                                        · {boardText(s.board)}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                        {selectedLesson.description && (
                          <div>
                            <p className="text-[10px] uppercase text-muted-foreground font-semibold">
                              Obs
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {selectedLesson.description}
                            </p>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Attendance */}
                  {selectedLesson.student && selectedLesson.teacher && (
                    <div className="border-t pt-3">
                      {selectedLesson.attendance_status === 'pending' ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() =>
                              handleCheckin(selectedLesson.id, 'mark_present')
                            }
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Presente
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              handleCheckin(selectedLesson.id, 'mark_absent')
                            }
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Faltou
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <Badge
                            variant={
                              selectedLesson.attendance_status === 'present'
                                ? 'default'
                                : 'destructive'
                            }
                          >
                            {selectedLesson.attendance_status === 'present'
                              ? 'Presente'
                              : 'Faltou'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() =>
                              handleCheckin(
                                selectedLesson.id,
                                'reset_attendance',
                              )
                            }
                          >
                            <RotateCcw className="w-3 h-3 mr-1" /> Resetar
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t pt-3 space-y-2">
                    {selectedLesson.pending_association && (
                      <Button
                        size="sm"
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        onClick={() => handleAssociate(selectedLesson)}
                      >
                        <UserPlus className="w-4 h-4 mr-1" /> Associar Professor
                      </Button>
                    )}
                    {/* Tirar só o aluno, mantendo a aula. Some quando há alunos
                        extras: liberar o titular deixaria a vaga "disponível"
                        com gente dentro — esses saem primeiro, na edição. */}
                    {selectedLesson.student &&
                      !selectedLesson.extra_students?.length && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => setLessonToRelease(selectedLesson.id)}
                        >
                          <UserMinus className="w-4 h-4 mr-1" /> Liberar vaga
                        </Button>
                      )}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() =>
                          router.visit(`/lessons/${selectedLesson.id}/edit`)
                        }
                      >
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-destructive"
                        onClick={() => handleDelete(selectedLesson.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Dialog: Delete */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir aula</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza? Esta acao nao pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Liberar vaga */}
      <AlertDialog
        open={lessonToRelease !== null}
        onOpenChange={(open) => {
          if (!open) setLessonToRelease(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Liberar vaga</AlertDialogTitle>
            <AlertDialogDescription>
              O aluno sai da aula e o crédito volta para o pacote dele. O
              professor, o dia e o horário ficam como estão, e a vaga volta a
              aparecer como disponível. O aluno é avisado no app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRelease}>
              Liberar vaga
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Checkin */}
      <AlertDialog
        open={confirmCheckinId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmCheckinId(null)
            setConfirmCheckinAction('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmCheckinAction === 'mark_present'
                ? 'Marcar presente'
                : confirmCheckinAction === 'mark_absent'
                  ? 'Marcar faltou'
                  : 'Resetar presenca'}
            </AlertDialogTitle>
            <AlertDialogDescription>Confirma esta acao?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCheckin}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Associate Teacher */}
      <Dialog open={associateOpen} onOpenChange={setAssociateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Associar Professor</DialogTitle>
            <DialogDescription>
              {selectedLesson?.student?.name} - {selectedLesson?.start_time}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-[50vh] overflow-y-auto">
            {teachers.map((t) => (
              <button
                key={t.id}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${associateTeacherId === t.id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}
                onClick={() => setAssociateTeacherId(t.id)}
              >
                {t.name}
                {selectedLesson?.source_teacher?.id === t.id && (
                  <span className="ml-2 text-xs opacity-70">
                    vaga de origem
                  </span>
                )}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssociateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmAssociate} disabled={!associateTeacherId}>
              Associar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Quick Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Plus className="w-5 h-5 inline mr-1" />{' '}
              {createMode === 'availability'
                ? 'Disponibilizar Horario'
                : 'Agendar Aula'}
            </DialogTitle>
            <DialogDescription>
              {format(
                new Date(selectedDate + 'T00:00:00'),
                "EEEE, dd 'de' MMMM",
                { locale: ptBR },
              )}{' '}
              - {createStartTime} ate {createEndTime}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex rounded-lg border overflow-hidden">
              <button
                className={`flex-1 py-2 text-sm font-medium transition-colors ${createMode === 'availability' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                onClick={() => setCreateMode('availability')}
              >
                Disponibilizar
              </button>
              <button
                className={`flex-1 py-2 text-sm font-medium transition-colors ${createMode === 'lesson' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                onClick={() => setCreateMode('lesson')}
              >
                Agendar Aula
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Inicio</Label>
                <Input
                  type="time"
                  value={createStartTime}
                  onChange={(e) => setCreateStartTime(e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Fim</Label>
                <Input
                  type="time"
                  value={createEndTime}
                  onChange={(e) => setCreateEndTime(e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>
            </div>
            {createMode === 'availability' ? (
              <div>
                <Label>Professores</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {teachers.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={createTeacherIds.includes(String(t.id))}
                        onCheckedChange={() =>
                          toggleCreateTeacher(String(t.id))
                        }
                      />
                      <span className="text-sm">{t.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label>Professor</Label>
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {teachers.map((t) => (
                      <button
                        key={t.id}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${createTeacherId === String(t.id) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                        onClick={() => setCreateTeacherId(String(t.id))}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Aluno (opcional)</Label>
                  <div className="border rounded-lg mt-1 overflow-hidden">
                    <Input
                      value={createStudentFilter}
                      onChange={(e) => setCreateStudentFilter(e.target.value)}
                      placeholder="Buscar aluno..."
                      className="border-0 border-b rounded-none focus-visible:ring-0"
                    />
                    <div className="max-h-32 overflow-y-auto">
                      {students
                        .filter(
                          (s) =>
                            !createStudentFilter ||
                            s.name
                              .toLowerCase()
                              .includes(createStudentFilter.toLowerCase()),
                        )
                        .map((s) => (
                          <button
                            key={s.id}
                            className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 transition-colors ${createStudentId === String(s.id) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            onClick={() => setCreateStudentId(String(s.id))}
                          >
                            {s.name}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={submitQuickCreate}>
              {createMode === 'availability' ? 'Disponibilizar' : 'Agendar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Bulk Delete */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Excluir {selectedSlotIds.length} aula(s)
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir {selectedSlotIds.length}{' '}
              agendamento(s)? Essa acao nao pode ser desfeita.
              {selectedSlotIds.some(
                (id) => dayLessons.find((l) => l.id === id)?.student,
              ) && (
                <span className="block mt-2 text-destructive font-medium">
                  Atencao: algumas aulas selecionadas tem alunos agendados.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir {selectedSlotIds.length}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Operating Hours */}
      <Dialog open={hoursOpen} onOpenChange={setHoursOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Settings className="w-5 h-5 inline mr-1" /> Horario de
              Funcionamento
            </DialogTitle>
            <DialogDescription>
              Configure os dias e horarios que a escola funciona
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Dias da Semana</Label>
              <div className="flex gap-2 mt-2">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'].map(
                  (name, i) => (
                    <button
                      key={i}
                      className={`w-10 h-10 rounded-lg text-xs font-medium transition-colors ${opDays.includes(i) ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                      onClick={() => toggleOpDay(i)}
                    >
                      {name}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manha - Inicio</Label>
                <Input
                  type="time"
                  value={morningStart}
                  onChange={(e) => setMorningStart(e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Manha - Fim</Label>
                <Input
                  type="time"
                  value={morningEnd}
                  onChange={(e) => setMorningEnd(e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tarde - Inicio</Label>
                <Input
                  type="time"
                  value={afternoonStart}
                  onChange={(e) => setAfternoonStart(e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Tarde - Fim</Label>
                <Input
                  type="time"
                  value={afternoonEnd}
                  onChange={(e) => setAfternoonEnd(e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveOperatingHours}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Replicate */}
      <Dialog open={replicateOpen} onOpenChange={setReplicateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Copy className="w-5 h-5 inline mr-1" /> Replicar Horarios
            </DialogTitle>
            <DialogDescription>
              {selectedSlotIds.length} horario(s) de{' '}
              {format(new Date(selectedDate + 'T00:00:00'), 'dd/MM', {
                locale: ptBR,
              })}{' '}
              serao copiados para os dias selecionados.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Selecione os dias destino</Label>
            <div className="mt-2 grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {replicateDateOptions.map((date) => {
                const d = new Date(date + 'T00:00:00')
                const isSelected = replicateTargetDates.includes(date)
                return (
                  <button
                    key={date}
                    className={`text-center p-2 rounded-lg border text-xs transition-colors ${isSelected ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}
                    onClick={() => toggleReplicateDate(date)}
                  >
                    <p className="font-medium">
                      {format(d, 'EEE', { locale: ptBR })}
                    </p>
                    <p className="text-[11px]">{format(d, 'dd/MM')}</p>
                  </button>
                )
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplicateOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmReplicate}
              disabled={replicateTargetDates.length === 0}
            >
              <Copy className="w-4 h-4 mr-1" /> Replicar para{' '}
              {replicateTargetDates.length} dia(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick add aluno a uma vaga vazia */}
      <Dialog
        open={quickAddVagaId !== null}
        onOpenChange={(open) => {
          if (!open) setQuickAddVagaId(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar aluno à vaga</DialogTitle>
            <DialogDescription>
              Escolha o aluno que vai ocupar esta vaga. O sistema usa
              automaticamente o pacote ativo dele.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              placeholder="Buscar aluno por nome..."
              value={quickAddSearch}
              onChange={(e) => setQuickAddSearch(e.target.value)}
              autoFocus
            />
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {students
                .filter(
                  (s) =>
                    !quickAddSearch ||
                    s.name.toLowerCase().includes(quickAddSearch.toLowerCase()),
                )
                .map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-md border hover:bg-muted text-sm"
                    onClick={() => {
                      if (!quickAddVagaId) return
                      router.patch(
                        `/lessons/${quickAddVagaId}`,
                        { lesson: { student_id: s.id } },
                        {
                          preserveScroll: true,
                          preserveState: true,
                          onSuccess: () => {
                            toast.success(`${s.name} adicionado(a) à vaga`)
                            setQuickAddVagaId(null)
                            setQuickAddSearch('')
                            router.reload({ only: ['lessons_by_date'] })
                          },
                          onError: () => toast.error('Erro ao adicionar aluno'),
                        },
                      )
                    }}
                  >
                    {s.name}
                  </button>
                ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
